import type { LDMultiKindContext } from 'launchdarkly-react-client-sdk';
import { useCallback } from 'react';

import { ANONYMOUS_CONTEXT_KEY } from '@/constants/launchDarkly';
import {
  ldClient,
  useLaunchDarklyIdentity,
} from '@/providers/LaunchDarklyProvider.web';
import type { LDContext } from '@/types/launchDarkly';
import { isRandomizationUnitKey } from '@/utils/guestLdIdentity';
import {
  buildLdContext,
  flattenLdContext,
} from '@/utils/launchDarklyUtils.web';
import { Logger } from '@/utils/logging';

/**
 * Hook to update LaunchDarkly user context (Web)
 *
 * @example
 * const { updateContext } = useLaunchDarklyContext();
 *
 * // After user logs in
 * await updateContext({
 *   kind: 'user',
 *   key: user.id,
 *   email: user.email,
 *   name: user.name,
 * });
 *
 * @see docs/FEATURE_FLAGS_LD.md - Complete LaunchDarkly integration guide
 */
export const useLaunchDarklyContext = () => {
  const { isIdentified, guestIdentity, setIsIdentified } =
    useLaunchDarklyIdentity();

  const getContext = () => ldClient?.getContext() || null;

  const updateContext = useCallback(
    async (contextOrUpdater: LDContext | ((prev: LDContext) => LDContext)) => {
      // Unconfigured: the provider already logged the missing client-side ID.
      if (!ldClient) return;

      try {
        // ldClient.getContext() returns the multi-kind shape LD stores
        // ({kind:'multi', user:{key,...}, ...}), not the flat `LDContext`
        // shape callers merge onto via `updateContext((prev) => ({...prev,
        // shopId}))`. flattenLdContext() converts it back so `key` (which
        // lives at `user.key`, not top-level) survives the merge — otherwise
        // buildLdContext builds a context with no key and the LD SDK's
        // anonymous fallback mints and permanently caches an unrelated
        // random key, rebucketing the session into a different experiment
        // arm.
        const rawContext = ldClient.getContext() as
          | LDMultiKindContext
          | LDContext
          | null;
        const prev =
          rawContext?.kind === 'multi'
            ? flattenLdContext(rawContext, ANONYMOUS_CONTEXT_KEY)
            : rawContext || {
                kind: 'user',
                key: ANONYMOUS_CONTEXT_KEY,
              };

        const context =
          typeof contextOrUpdater === 'function'
            ? contextOrUpdater(prev)
            : contextOrUpdater;

        const ldContext = buildLdContext(context);
        await ldClient.identify(ldContext);

        // Off the context that landed, not the caller's intent.
        setIsIdentified(
          isRandomizationUnitKey(
            flattenLdContext(ldContext, ANONYMOUS_CONTEXT_KEY).key,
          ),
        );
        Logger.console('LaunchDarkly context identified (Web)', { context });
      } catch (error) {
        Logger.error(
          'Failed to update LaunchDarkly context (Web):',
          error instanceof Error
            ? { message: error.message, name: error.name }
            : { error: String(error) },
        );
      }
    },
    [setIsIdentified],
  );

  return { getContext, updateContext, isIdentified, guestIdentity };
};
